import React, { useEffect, useRef, useState } from "react";
import AuthTextField from "../../components/Form/AuthTextField";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import Spinner from "../../components/Spinner/Spinner";
import PaperVideoStepIndicator, {
  type PaperVideoStep,
} from "./PaperVideoStepIndicator";
import { formatFileSize } from "./formatFileSize";
import {
  checkPaperVideoExists,
  confirmPaperVideoUpload,
  putFileToPresignedUrlWithProgress,
  requestPaperVideoPresign,
  requestPaperVideoUpdateCode,
  updatePaperVideoDetails,
  validatePaperVideoFile,
  verifyPaperVideoUploadCode,
  type PaperVideoRecord,
} from "../../api/paperVideos";

type Step = "lookup" | "edit" | "verify" | "done";

const primaryBtnClass =
  "eh-btn-primary block w-full py-3 text-sm font-semibold text-center disabled:opacity-60 disabled:cursor-not-allowed";

interface Props {
  /** Seeded from the "Upload new video" tab when it finds the paper ID
   * already has a submission — bump `nonce` to re-trigger even if the paper
   * ID string repeats. */
  initialPaperId?: { paperId: string; nonce: number } | null;
}

export default function PaperVideoUpdateFlow({ initialPaperId }: Props) {
  const [step, setStep] = useState<Step>("lookup");
  const [paperId, setPaperId] = useState("");
  const [paperTitle, setPaperTitle] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [email, setEmail] = useState("");
  const [conferenceCode, setConferenceCode] = useState("");
  // Lowercased email on file at the time this paper ID was looked up. Used to
  // detect whether the user is changing it (which requires verification).
  const [originalEmail, setOriginalEmail] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [updatedVideo, setUpdatedVideo] = useState<PaperVideoRecord | null>(
    null,
  );
  const [didReplaceVideo, setDidReplaceVideo] = useState(false);

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [overwriteConfirmed, setOverwriteConfirmed] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const emailChangedNow =
    originalEmail !== null && email.trim().toLowerCase() !== originalEmail;

  const steps: PaperVideoStep[] = emailChangedNow
    ? [
        { id: "lookup", label: "Find submission" },
        { id: "edit", label: "Edit details" },
        { id: "verify", label: "Verify email" },
        { id: "done", label: "Done" },
      ]
    : [
        { id: "lookup", label: "Find submission" },
        { id: "edit", label: "Edit details" },
        { id: "done", label: "Done" },
      ];

  function clearFeedback() {
    setError("");
    setMessage("");
  }

  function resetAll() {
    setStep("lookup");
    setPaperId("");
    setPaperTitle("");
    setAuthorName("");
    setEmail("");
    setConferenceCode("");
    setOriginalEmail(null);
    setCode("");
    setVideoFile(null);
    setOverwriteConfirmed(false);
    setUploadProgress(0);
    setUpdatedVideo(null);
    setDidReplaceVideo(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    clearFeedback();
  }

  async function lookupPaperId(trimmed: string) {
    clearFeedback();
    setIsLookingUp(true);
    try {
      const result = await checkPaperVideoExists(trimmed);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      if (!result.exists) {
        setError(
          'No submission found for this paper ID. Use the "Upload new video" tab instead.',
        );
        return;
      }
      setPaperId(trimmed);
      setAuthorName(result.authorName ?? "");
      setPaperTitle(result.paperTitle ?? "");
      setEmail(result.authorEmail ?? "");
      setOriginalEmail(result.authorEmail?.toLowerCase() ?? null);
      setConferenceCode(result.conferenceCode ?? "");
      setStep("edit");
    } finally {
      setIsLookingUp(false);
    }
  }

  // Auto-lookup when handed off from the "Upload new video" tab. Only
  // initialPaperId?.nonce should retrigger this — lookupPaperId is a stable
  // function of state already captured in the closure at call time.
  const initialPaperIdNonce = initialPaperId?.nonce;
  useEffect(() => {
    if (!initialPaperId) return;
    setPaperId(initialPaperId.paperId);
    void lookupPaperId(initialPaperId.paperId);
    // initialPaperIdNonce drives this effect intentionally on its own.
  }, [initialPaperIdNonce]);

  async function handleLookup(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = paperId.trim();
    if (!trimmed) {
      clearFeedback();
      setError("Please enter the paper ID.");
      return;
    }
    await lookupPaperId(trimmed);
  }

  async function finalize(emailWasChanged: boolean) {
    setIsSaving(true);
    try {
      const trimmedEmail = email.trim();
      const trimmedTitle = paperTitle.trim();
      const trimmedName = authorName.trim();
      let latestVideo: PaperVideoRecord | null = null;

      if (emailWasChanged) {
        const result = await updatePaperVideoDetails({
          paperId,
          paperTitle: trimmedTitle,
          authorName: trimmedName,
          email: trimmedEmail,
        });
        if (!result.ok) {
          setError(result.message ?? "Failed to save changes.");
          return;
        }
        latestVideo = result.data.video;
      }

      if (videoFile) {
        setUploadProgress(0);
        const presign = await requestPaperVideoPresign({
          paperId,
          email: trimmedEmail,
          authorName: trimmedName,
          conferenceCode,
          contentType: videoFile.type,
          fileSizeBytes: videoFile.size,
          fileName: videoFile.name,
        });
        if (!presign.ok) {
          setError(presign.message ?? "Failed to prepare upload.");
          return;
        }
        await putFileToPresignedUrlWithProgress(
          presign.data.uploadUrl,
          videoFile,
          setUploadProgress,
        );
        const confirmed = await confirmPaperVideoUpload({
          paperId,
          paperTitle: trimmedTitle,
          email: trimmedEmail,
          authorName: trimmedName,
          conferenceCode,
          s3Key: presign.data.s3Key,
          contentType: videoFile.type,
          fileSizeBytes: videoFile.size,
          originalFileName: videoFile.name,
        });
        if (!confirmed.ok) {
          setError(confirmed.message ?? "Failed to confirm upload.");
          return;
        }
        latestVideo = confirmed.data.video;
      } else if (!emailWasChanged) {
        const result = await updatePaperVideoDetails({
          paperId,
          paperTitle: trimmedTitle,
          authorName: trimmedName,
          email: trimmedEmail,
        });
        if (!result.ok) {
          setError(result.message ?? "Failed to save changes.");
          return;
        }
        latestVideo = result.data.video;
      }

      setDidReplaceVideo(Boolean(videoFile));
      setUpdatedVideo(latestVideo);
      setStep("done");
    } catch (err) {
      console.error(err);
      setError(
        "Failed to save changes. Please check your connection and try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSubmitEdit(e: React.FormEvent) {
    e.preventDefault();
    clearFeedback();
    if (!paperTitle.trim()) {
      setError("Please enter the paper title.");
      return;
    }
    if (!authorName.trim()) {
      setError("Please enter your name.");
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (videoFile && !overwriteConfirmed) {
      setError(
        "Please confirm you want to overwrite the existing video with this file.",
      );
      return;
    }

    if (!emailChangedNow) {
      await finalize(false);
      return;
    }

    setIsSendingCode(true);
    try {
      const response = await requestPaperVideoUpdateCode({
        paperId,
        email: trimmedEmail,
      });
      if (response.ok) {
        setStep("verify");
        setMessage("We sent a 6-digit verification code to your new email.");
      } else {
        setError(response.message ?? "Failed to send verification code.");
      }
    } finally {
      setIsSendingCode(false);
    }
  }

  async function handleResendCode() {
    clearFeedback();
    setIsSendingCode(true);
    try {
      const response = await requestPaperVideoUpdateCode({
        paperId,
        email: email.trim(),
      });
      if (response.ok) {
        setMessage("A new verification code has been sent.");
      } else {
        setError(response.message ?? "Failed to resend code.");
      }
    } finally {
      setIsSendingCode(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    clearFeedback();
    if (!/^\d{6}$/.test(code)) {
      setError("Please enter the 6-digit code from your email.");
      return;
    }
    setIsVerifying(true);
    try {
      const response = await verifyPaperVideoUploadCode({
        paperId,
        email: email.trim(),
        code,
      });
      if (response.ok) {
        await finalize(true);
      } else {
        setError(response.message ?? "Code verification failed.");
      }
    } finally {
      setIsVerifying(false);
    }
  }

  function handleBackToEdit() {
    setStep("edit");
    clearFeedback();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    clearFeedback();
    const selected = e.target.files?.[0] ?? null;
    setOverwriteConfirmed(false);
    if (!selected) {
      setVideoFile(null);
      return;
    }
    const validationError = validatePaperVideoFile(selected);
    if (validationError) {
      setError(validationError);
      setVideoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setVideoFile(selected);
  }

  const isBusy = isSendingCode || isSaving;

  return (
    <div>
      <PaperVideoStepIndicator steps={steps} currentStep={step} />

      {step === "lookup" && (
        <form onSubmit={handleLookup} onChange={clearFeedback}>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Paper ID
            </span>
            <input
              type="text"
              value={paperId}
              onChange={(e) => setPaperId(e.target.value)}
              required
              className="w-full border border-slate-300 bg-white px-3 py-2.5 text-sm rounded-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
            />
            <span className="block text-xs text-slate-500 mt-1">
              Enter the paper ID for a submission you've already uploaded.
            </span>
          </label>
          <button type="submit" className={primaryBtnClass} disabled={isLookingUp}>
            {isLookingUp ? <Spinner /> : "Look up submission"}
          </button>
        </form>
      )}

      {step === "edit" && (
        <form onSubmit={handleSubmitEdit} onChange={clearFeedback}>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Paper ID
            </span>
            <input
              type="text"
              value={paperId}
              readOnly
              aria-readonly="true"
              className="w-full border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm rounded-sm text-slate-600 cursor-not-allowed"
            />
          </label>
          <AuthTextField
            label="Paper title"
            value={paperTitle}
            onChange={setPaperTitle}
            required
          />
          <AuthTextField
            label="Your name"
            value={authorName}
            onChange={setAuthorName}
            required
          />
          <AuthTextField
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
            hint="Changing your email requires verifying the new address."
          />

          <div className="border border-slate-200 rounded-sm p-4 mb-4">
            <p className="text-sm font-medium text-slate-700 mb-2">
              Replace video (optional)
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={handleFileChange}
              className="block w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-slate-800 file:text-white file:text-sm file:font-medium file:cursor-pointer"
            />
            <span className="block text-xs text-slate-500 mt-1">
              MP4, MOV, or WebM. Up to 400 MB. Leave empty to keep your current
              video.
            </span>
            {videoFile && (
              <div className="mt-3">
                <p className="text-sm text-slate-600 mb-2">
                  {videoFile.name} — {formatFileSize(videoFile.size)}
                </p>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={overwriteConfirmed}
                    onChange={(e) => setOverwriteConfirmed(e.target.checked)}
                  />
                  <span>Yes, overwrite the existing video with this file</span>
                </label>
              </div>
            )}
          </div>

          {isSaving && videoFile && (
            <div className="mb-4">
              <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-800 transition-all"
                  style={{ width: `${Math.round(uploadProgress * 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Uploading… {Math.round(uploadProgress * 100)}%
              </p>
            </div>
          )}

          <button type="submit" className={primaryBtnClass} disabled={isBusy}>
            {isBusy ? (
              <Spinner />
            ) : emailChangedNow ? (
              "Verify new email"
            ) : (
              "Save changes"
            )}
          </button>
          <button
            type="button"
            onClick={resetAll}
            disabled={isBusy}
            className="block mx-auto text-sm text-slate-700 underline hover:text-slate-900 font-medium mt-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
          >
            Look up a different paper ID
          </button>
        </form>
      )}

      {step === "verify" && (
        <form onSubmit={handleVerifyCode} onChange={clearFeedback}>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700 mb-4">
            <p className="font-medium text-slate-900 mb-1">Check your inbox</p>
            <p>
              Enter the 6-digit code we sent to <strong>{email}</strong> to
              confirm this new email address.
            </p>
          </div>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Verification code
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="w-full text-center text-2xl tracking-[0.4em] border border-slate-300 bg-white px-3 py-3 rounded-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              required
            />
          </label>
          {isSaving && videoFile && (
            <div className="mb-4">
              <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-800 transition-all"
                  style={{ width: `${Math.round(uploadProgress * 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Uploading… {Math.round(uploadProgress * 100)}%
              </p>
            </div>
          )}
          <button
            type="submit"
            className={primaryBtnClass}
            disabled={isVerifying || isSaving}
          >
            {isVerifying || isSaving ? <Spinner /> : "Verify and save"}
          </button>
          <div className="flex flex-col sm:flex-row gap-3 justify-center text-sm mt-4">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isSendingCode || isVerifying || isSaving}
              className="text-slate-700 underline hover:text-slate-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              {isSendingCode ? "Sending…" : "Resend code"}
            </button>
            <button
              type="button"
              onClick={handleBackToEdit}
              disabled={isSendingCode || isVerifying || isSaving}
              className="text-slate-700 underline hover:text-slate-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              Change details
            </button>
          </div>
        </form>
      )}

      {step === "done" && updatedVideo && (
        <div>
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-sm text-green-900 mb-4">
            <p className="font-medium mb-1">Details updated</p>
            <p>
              Your submission for paper <strong>{updatedVideo.paperId}</strong>{" "}
              has been updated.{" "}
              {didReplaceVideo
                ? "Your video file has been replaced."
                : "Your video file was not changed."}
            </p>
          </div>
          <dl className="text-sm text-slate-700 space-y-1 mb-6">
            <div>
              <dt className="inline font-medium">Paper title: </dt>
              <dd className="inline">{updatedVideo.paperTitle}</dd>
            </div>
            <div>
              <dt className="inline font-medium">Name: </dt>
              <dd className="inline">{updatedVideo.authorName}</dd>
            </div>
            <div>
              <dt className="inline font-medium">Email: </dt>
              <dd className="inline">{updatedVideo.authorEmail}</dd>
            </div>
            {didReplaceVideo && (
              <div>
                <dt className="inline font-medium">File: </dt>
                <dd className="inline">
                  {updatedVideo.originalFileName} (
                  {formatFileSize(updatedVideo.fileSizeBytes)})
                </dd>
              </div>
            )}
          </dl>
          <button
            type="button"
            className="eh-btn-secondary w-full py-3 text-sm font-semibold text-center"
            onClick={resetAll}
          >
            Update another submission
          </button>
        </div>
      )}

      {error && <ErrorAlert className="mt-4">{error}</ErrorAlert>}
      {message && step !== "done" && (
        <p className="text-green-700 text-sm mt-4">{message}</p>
      )}
    </div>
  );
}
